@if (loading) {
  <mat-progress-bar mode="indeterminate" data-testid="compliance-loading"></mat-progress-bar>
}
@if (!loading && complianceData && activePlanPage) {
  <div
    class="compliance-table-wrapper"
    #scrollContainer
    (scroll)="onScrollPositionChange()"
    data-testid="compliance-report-table"
    [attr.data-project-id]="activePlanPage.getArchitecturalPlanId()"
    [attr.data-file-id]="getActiveFileId()"
    [attr.data-page-number]="activePlanPage.getPageNumber()"
    [attr.data-page-title]="activePlanPage.getTitle()"
    aria-busy="false"
  >
    <table mat-table [dataSource]="visibleSections" class="compliance-table" [class.scrolled]="isScrolled">
      <!-- Section Column -->
      <ng-container matColumnDef="section">
        <th mat-header-cell *matHeaderCellDef>
          <div style="display: flex; align-items: center; gap: 8px">
            <span>Section</span>
            <button
              mat-icon-button
              (click)="openAddComplianceDialog()"
              matTooltip="Run additional compliance analysis"
              class="add-compliance-btn"
            >
              <mat-icon>add_circle_outline</mat-icon>
            </button>
            <button
              mat-icon-button
              type="button"
              (click)="setAllExpanded(true)"
              aria-label="Expand All"
              title="Expand All"
              class="expand-collapse-all-btn"
            >
              <span class="material-icons">unfold_more</span>
            </button>
            <button
              mat-icon-button
              type="button"
              (click)="setAllExpanded(false)"
              aria-label="Collapse All"
              title="Collapse All"
              class="expand-collapse-all-btn"
            >
              <span class="material-icons">unfold_less</span>
            </button>
          </div>
        </th>
        <td
          mat-cell
          *matCellDef="let section; let i = index"
          class="hierarchy-cell"
          [style.padding-left]="getSectionPadding(section.depth)"
        >
          <div style="display: flex; align-items: center; width: 100%; min-width: 0">
            @if (hasChildren(section)) {
              <span
                class="material-icons expander"
                data-testid="compliance-section-expander"
                [attr.data-section-id]="section.sectionId"
                (click)="toggleRow(section, $event)"
                (keydown.enter)="toggleRow(section, $event)"
                (keydown.space)="toggleRow(section, $event); $event.preventDefault()"
                [attr.aria-label]="(isExpanded(section) ? 'Collapse ' : 'Expand ') + section.title"
                [attr.title]="isExpanded(section) ? 'Collapse' : 'Expand'"
                style="cursor: pointer; user-select: none; flex-shrink: 0"
                tabindex="0"
                role="button"
              >
                {{ isExpanded(section) ? 'expand_more' : 'chevron_right' }}
              </span>
            }
            @if (!hasChildren(section)) {
              <span class="expander-placeholder" style="display: inline-block; width: 24px; flex-shrink: 0"></span>
            }
            <!-- NEW: Section selection area with hover-to-checkbox pattern -->
            @if (section.sectionId) {
              <span class="section-selection-area overlay-area" style="flex-shrink: 0">
                <mat-icon class="section-icon-placeholder">{{ getComplianceIcon(section.status) }}</mat-icon>
                <mat-checkbox
                  class="section-checkbox overlay-checkbox"
                  [(ngModel)]="section.selected"
                  (click)="$event.stopPropagation()"
                  (change)="onSectionCheckboxChange(section)"
                  [color]="'accent'"
                >
                </mat-checkbox>
              </span>
            }
            <div class="section-title-group" style="flex: 1 1 auto; min-width: 0">
              <div class="title-line">
                @let titleParts = getSectionTitleParts(section.title);
                @if (section.sectionId) {
                  <a
                    class="section-title"
                    data-testid="compliance-code-link"
                    [attr.data-section-id]="section.sectionId"
                    [href]="getIccUrlForSection(section)"
                    target="_blank"
                    rel="noopener noreferrer"
                    [matTooltip]="section.title"
                  >
                    <span class="section-number">{{ titleParts.number }}</span>
                    @if (titleParts.name) {
                      <span class="section-name">{{ titleParts.name }}</span>
                    }
                  </a>
                }
                @if (!section.sectionId) {
                  <span class="section-title" [matTooltip]="section.title">
                    <span class="section-number">{{ titleParts.number }}</span>
                    @if (titleParts.name) {
                      <span class="section-name">{{ titleParts.name }}</span>
                    }
                  </span>
                }
              </div>
              @if (section.hierarchy && section.hierarchy.length > 1) {
                <div class="hierarchy-line">
                  @for (h of section.hierarchy.slice(0, -1); track h; let last = $last; let i = $index) {
                    @if (h.id) {
                      <a
                        class="hierarchy-item hierarchy-link"
                        [href]="getIccUrlForHierarchyItem(section, h, i)"
                        target="_blank"
                        rel="noopener noreferrer"
                        [title]="h.title"
                      >
                        {{ h.title }}
                      </a>
                    }
                    @if (!h.id) {
                      <span class="hierarchy-item">{{ h.title }}</span>
                    }
                    @if (!last) {
                      <span class="hierarchy-separator"> &gt; </span>
                    }
                  }
                </div>
              }
            </div>
          </div>
        </td>
      </ng-container>
      <!-- Relevance Column -->
      <ng-container matColumnDef="relevance">
        <th mat-header-cell *matHeaderCellDef class="mat-column-relevance">
          <div class="relevance-header">
            <span>Relevance</span>
            <app-column-filter
              [config]="relevanceFilterConfig"
              [criterion]="relevanceCriterion"
              (criterionChange)="onRelevanceCriterionChange($event)"
            ></app-column-filter>
          </div>
        </th>
        <td mat-cell *matCellDef="let section">
          <!-- Container nodes with '-' relevance show count chips -->
          @if (shouldShowRelevanceCountChips(section)) {
            @if (
              countDescendantRelevance(section).high +
                countDescendantRelevance(section).medium +
                countDescendantRelevance(section).low >
              0
            ) {
              <div class="relevance-count-chips">
                @for (
                  item of [
                    { type: 'high', count: countDescendantRelevance(section).high },
                    { type: 'medium', count: countDescendantRelevance(section).medium },
                    { type: 'low', count: countDescendantRelevance(section).low },
                  ];
                  track item
                ) {
                  @if (item.count > 0) {
                    <span class="relevance-chip relevance-count-chip" [class]="'relevance-' + item.type">
                      {{ item.count }}
                    </span>
                  }
                }
              </div>
            } @else {
              <span class="relevance-chip relevance-count-chip relevance-not-relevant">—</span>
            }
          } @else {
            @if (section.rationale) {
              <button
                mat-button
                class="relevance-chip"
                data-testid="compliance-rationale-trigger"
                [attr.data-section-id]="section.sectionId"
                [attr.aria-label]="'Open rationale for ' + section.title"
                [class]="'relevance-' + section.relevance.toLowerCase().replace(' ', '-')"
                (click)="openRationaleDialog(section)"
              >
                {{ section.relevance }}
              </button>
            }
            @if (!section.rationale) {
              <span class="relevance-chip" [class]="'relevance-' + section.relevance.toLowerCase().replace(' ', '-')">
                {{ section.relevance }}
              </span>
            }
          }
        </td>
      </ng-container>
      <!-- Compliance Column -->
      <ng-container matColumnDef="compliance">
        <th mat-header-cell *matHeaderCellDef class="mat-column-compliance">
          <div class="compliance-header">
            <span>Compliance</span>
            <app-column-filter
              [config]="complianceFilterConfig"
              [criterion]="complianceCriterion"
              (criterionChange)="onComplianceCriterionChange($event)"
            ></app-column-filter>
          </div>
        </th>
        <td mat-cell *matCellDef="let section">
          @if (section.status !== '-') {
            <span>
              @if (section.status !== 'Not Applicable') {
                <button
                  mat-button
                  class="compliance-chip"
                  data-testid="compliance-report-trigger"
                  [attr.data-section-id]="section.sectionId"
                  [attr.aria-label]="'Open compliance report for ' + section.title"
                  [ngClass]="getComplianceClass(section.status)"
                  (click)="openComplianceReport(section)"
                >
                  {{ section.status | complianceRiskLabel }}
                </button>
              }
              @if (section.status === 'Not Applicable') {
                <span class="compliance-chip" [ngClass]="getComplianceClass(section.status)">
                  {{ section.status | complianceRiskLabel }}
                </span>
              }
            </span>
          }
          @if (section.status === '-') {
            <span>-</span>
          }
        </td>
      </ng-container>
      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
      <tr
        mat-row
        *matRowDef="let row; columns: displayedColumns; let i = index"
        [ngClass]="{ 'section-selected': row.selected }"
        data-testid="compliance-section-row"
        [attr.data-section-id]="row.sectionId"
        [attr.data-section-title]="row.title"
        [attr.data-parent-section-id]="getParentSectionId(row)"
        [attr.data-depth]="row.depth"
        [attr.data-relevance]="row.relevance"
        [attr.data-status]="row.status"
        [@rowAnimation]
      ></tr>
    </table>
  </div>
}
<!-- Active task progress indicator -->
@if (!loading && !complianceData && hasActiveCodeApplicabilityTask() && activePlanPage) {
  @let activeTask = getActiveCodeApplicabilityTask();
  <div
    class="active-task-container"
    data-testid="compliance-active-task"
    role="status"
    aria-live="polite"
    aria-atomic="true"
  >
    <mat-card class="active-task-card">
      <mat-card-header>
        <div mat-card-avatar class="task-avatar">
          <mat-icon>sync</mat-icon>
        </div>
        <mat-card-title>Code Applicability Analysis in Progress</mat-card-title>
        <mat-card-subtitle
          >Analyzing page {{ activePlanPage.getPageNumber() }}: {{ activePlanPage.getTitle() }}</mat-card-subtitle
        >
      </mat-card-header>
      <mat-card-content>
        <div class="task-progress-info">
          <p>
            Code applicability analysis is currently running for this page. Results will appear here when the analysis
            completes.
          </p>
          @if (activeTask) {
            <div class="progress-details">
              <p><strong>Progress:</strong> {{ activeTask.progress }}%</p>
              <p><strong>Current Step:</strong> {{ activeTask.message }}</p>
              <mat-progress-bar mode="determinate" [value]="activeTask.progress"></mat-progress-bar>
            </div>
          }
        </div>
      </mat-card-content>
      <mat-card-actions>
        <button mat-button (click)="navigateToActiveTask(activeTask?.taskId)">
          <mat-icon>visibility</mat-icon>
          View Task Details
        </button>
      </mat-card-actions>
    </mat-card>
  </div>
}

<!-- Phase 3: Enhanced error handling with fallback UI -->
@if (!loading && showFallbackUI && complianceError && activePlanPage && !hasActiveCodeApplicabilityTask()) {
  <div data-testid="compliance-recoverable-error">
    <app-compliance-fallback
      [errorCategory]="complianceError"
      [activePlanPage]="activePlanPage"
      (retry)="onRetryCompliance()"
      (contactSupport)="onContactSupport()"
      (startInsights)="onStartInsights($event)"
    >
    </app-compliance-fallback>
  </div>
}

<!-- Original empty state for cases where no fallback UI is needed -->
@if (!loading && !complianceData && !showFallbackUI && activePlanPage) {
  <div data-testid="compliance-empty-state">
    <p data-testid="compliance-empty-status" role="status" aria-live="polite">
      No saved applicability or compliance results are available for this page.
    </p>
    <button
      mat-stroked-button
      type="button"
      data-testid="compliance-empty-run-analysis"
      (click)="openAddComplianceDialog()"
    >
      <mat-icon>add_circle_outline</mat-icon>
      Run compliance analysis
    </button>
  </div>
}

<!-- NEW: Selected Sections Floating Toolbar -->
<app-selected-sections-toolbar
  [selectedSections]="selectedSections"
  [visible]="selectedSections.length > 0"
  (action)="onSelectedSectionsAction($event)"
>
</app-selected-sections-toolbar>
